import React, { useState, useCallback, useEffect } from 'react';
import { X, Upload, AlertCircle, Check, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { toast } from 'react-toastify';
import { useUploadGuestPhotoMutation } from '../../store/services/api';
import { Photo } from '../../types/photos';

interface UploadPhotoGuestProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  eventUrl: string;
  onPhotoUploadComplete?: () => void;
}

interface SelectedPhoto {
  id: string;
  file: File;
  preview: string;
  uploadProgress?: number;
  uploadStatus?: 'pending' | 'uploading' | 'success' | 'error';
  errorMessage?: string;
}

const MAX_PHOTOS = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB max file size
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif'
];

// Validate file before upload
const validateFile = (file: File) => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size must be less than ${(MAX_FILE_SIZE / (1024 * 1024)).toFixed(0)}MB`
    };
  }

  if (!ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error: 'Only JPG, PNG, HEIC and HEIF images are allowed'
    };
  }

  return { valid: true };
};

const UploadPhotoGuest = ({ isOpen, onClose, title, eventUrl, onPhotoUploadComplete }: UploadPhotoGuestProps) => {
  const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [showFormatWarning, setShowFormatWarning] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [overallProgress, setOverallProgress] = useState(0);

  // RTK Query hooks
  const [uploadGuestPhoto] = useUploadGuestPhotoMutation();

  // Aktif yükleme işlemlerini takip etmek için
  const activeUploads = React.useRef<{
    intervals: {[id: string]: ReturnType<typeof setInterval>}, 
    overallInterval?: ReturnType<typeof setInterval>,
    canceled: boolean,
    controller?: AbortController
  }>({
    intervals: {},
    canceled: false
  });

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Auto-hide warnings after 3 seconds
  useEffect(() => {
    let limitWarningTimer: ReturnType<typeof setTimeout>;
    let formatWarningTimer: ReturnType<typeof setTimeout>;
    
    if (showLimitWarning) {
      limitWarningTimer = setTimeout(() => {
        setShowLimitWarning(false);
      }, 3000);
    }
    
    if (showFormatWarning) {
      formatWarningTimer = setTimeout(() => {
        setShowFormatWarning(false);
      }, 3000);
    }
    
    return () => {
      if (limitWarningTimer) clearTimeout(limitWarningTimer);
      if (formatWarningTimer) clearTimeout(formatWarningTimer);
    };
  }, [showLimitWarning, showFormatWarning]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const allFiles = Array.from(e.dataTransfer.files);
    const validFiles = allFiles.filter(file => ALLOWED_MIME_TYPES.includes(file.type));
    
    if (validFiles.length < allFiles.length) {
      setShowFormatWarning(true);
    }
    
    if (validFiles.length > 0) {
      handleFiles(validFiles);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const allFiles = Array.from(e.target.files);
      const validFiles = allFiles.filter(file => ALLOWED_MIME_TYPES.includes(file.type));
      
      if (validFiles.length < allFiles.length) {
        setShowFormatWarning(true);
      }
      
      if (validFiles.length > 0) {
        handleFiles(validFiles);
      }
    }
  };

  const handleFiles = (files: File[]) => {
    if (selectedPhotos.length + files.length > MAX_PHOTOS) {
      setShowLimitWarning(true);
      
      const remainingSlots = MAX_PHOTOS - selectedPhotos.length;
      if (remainingSlots <= 0) return;
      
      files = files.slice(0, remainingSlots);
    }

    const validFiles = files.filter(file => {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return false;
      }
      return true;
    });

    const newPhotos = validFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      uploadProgress: 0,
      uploadStatus: 'pending' as const
    }));

    setSelectedPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (id: string) => {
    if (isUploading) return;
    
    setSelectedPhotos(prev => {
      const filtered = prev.filter(photo => photo.id !== id);
      const photo = prev.find(p => p.id === id);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return filtered;
    });
    
    if (showLimitWarning && selectedPhotos.length <= MAX_PHOTOS) {
      setShowLimitWarning(false);
    }
  };

  // Cancel all uploads ve kapatma fonksiyonu
  const handleClose = () => {
    // İptal işaretini ayarla
    activeUploads.current.canceled = true;
    
    // Abort controller'ı çağır
    if (activeUploads.current.controller) {
      activeUploads.current.controller.abort();
      activeUploads.current.controller = undefined;
    }
    
    // Tüm interval'leri temizle
    if (activeUploads.current.overallInterval) {
      clearInterval(activeUploads.current.overallInterval);
      activeUploads.current.overallInterval = undefined;
    }
    
    Object.values(activeUploads.current.intervals).forEach(interval => {
      clearInterval(interval);
    });
    activeUploads.current.intervals = {};
    
    // Yükleme durumunu resetle
    setIsUploading(false);
    setUploadComplete(false);
    setUploadError(null);
    setOverallProgress(0);
    
    // Modalı kapat
    onClose();
  };

  const uploadPhoto = async (photo: SelectedPhoto) => {
    // Eğer iptal edildiyse hemen çık
    if (activeUploads.current.canceled) {
      return false;
    }

    // İptal için controller oluştur
    const controller = new AbortController();
    activeUploads.current.controller = controller;

    try {
      console.log('Uploading photo to:', eventUrl);
      
      // Upload başlangıcında progress %0 olarak ayarlayalım
      setSelectedPhotos(prev =>
        prev.map(p =>
          p.id === photo.id
            ? { ...p, uploadProgress: 0, uploadStatus: 'uploading' }
            : p
        )
      );
      
      // Daha basit bir progress simülasyonu
      let currentProgress = 0;
      const progressInterval = setInterval(() => {
        // İptal edildi mi kontrol et
        if (activeUploads.current.canceled) {
          clearInterval(progressInterval);
          // Upload'ı iptal et
          controller.abort();
          return;
        }
        
        // Rastgele artış (5-10 arası)
        currentProgress += 5 + Math.floor(Math.random() * 6);
        
        // %90'ı geçmesin
        if (currentProgress > 90) {
          clearInterval(progressInterval);
          currentProgress = 90;
        }
        
        // Progress'i güncelle
        console.log('Setting progress:', currentProgress);
        
        // Doğrudan DOM'u güncelle (Debug için)
        const progressEl = document.getElementById(`progress-${photo.id}`);
        if (progressEl) {
          progressEl.style.width = `${currentProgress}%`;
          
          const textEl = document.getElementById(`progress-text-${photo.id}`);
          if (textEl) {
            textEl.textContent = `${currentProgress}%`;
          }
        }
        
        // State güncelle
        setSelectedPhotos(prev => 
          prev.map(p => 
            p.id === photo.id 
              ? { ...p, uploadProgress: currentProgress, uploadStatus: 'uploading' } 
              : p
          )
        );
      }, 300);
      
      // Interval'i kaydet
      activeUploads.current.intervals[photo.id] = progressInterval;
      
      // Eğer iptal edildiyse hemen çık
      if (activeUploads.current.canceled) {
        clearInterval(progressInterval);
        controller.abort();
        return false;
      }
      
      // Gerçek upload
      const result = await uploadGuestPhoto({
        url: eventUrl,
        file: photo.file,
        onProgress: (progress: number) => {
          // RTK Query'nin desteklediği bir özellik olmasa da, gelecekte kullanılabilir
          console.log("API progress:", progress);
        }
      }).unwrap();
      
      // Simülasyonu durdur
      clearInterval(progressInterval);
      delete activeUploads.current.intervals[photo.id];
      
      // Eğer iptal edildiyse success gösterme
      if (activeUploads.current.canceled) {
        return false;
      }
      
      // Check if result has the success property (from the API response)
      if (result && 'success' in result && !result.success) {
        throw new Error('Upload failed');
      }

      // Başarılı olursa %100 göster
      setSelectedPhotos(prev =>
        prev.map(p =>
          p.id === photo.id
            ? { ...p, uploadProgress: 100, uploadStatus: 'success' }
            : p
        )
      );

      return true;
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.message || 'Upload failed';
      
      // Eğer iptal edildiyse hata mesajı gösterme
      if (!activeUploads.current.canceled) {
        setSelectedPhotos(prev =>
          prev.map(p =>
            p.id === photo.id
              ? { ...p, uploadStatus: 'error', errorMessage }
              : p
          )
        );
      }

      if (errorMessage.toLowerCase().includes('limit') ||
          errorMessage.toLowerCase().includes('credit') ||
          errorMessage.toLowerCase().includes('429') ||
          errorMessage.toLowerCase().includes('quota')) {
        return 'LIMIT_ERROR';
      }

      return false;
    }
  };

  const handleUpload = async () => {
    if (selectedPhotos.length === 0 || isUploading) return;

    // İptal edildi işaretini resetle
    activeUploads.current.canceled = false;
    
    setIsUploading(true);
    setUploadError(null);
    setUploadComplete(false);
    setOverallProgress(0);

    const pendingPhotos = selectedPhotos.filter(p => p.uploadStatus !== 'success');
    let successCount = 0;
    const totalCount = pendingPhotos.length;

    // Overall progress için zamanlayıcı (direkt DOM güncellemesi)
    let overallPercent = 0;
    const overallInterval = setInterval(() => {
      // İptal edildiyse çık
      if (activeUploads.current.canceled) {
        clearInterval(overallInterval);
        return;
      }
      
      // Her seferinde biraz artır
      overallPercent += 2;
      if (overallPercent > 95) {
        clearInterval(overallInterval);
        overallPercent = 95;  // En fazla %95'e kadar otomatik ilerlesin
      }
      
      console.log('Overall progress:', overallPercent);
      
      // State güncelle
      setOverallProgress(overallPercent);
      
      // DOM direkt güncelle
      const overallBar = document.querySelector('.overall-progress-bar');
      if (overallBar) {
        overallBar.setAttribute('style', `width: ${overallPercent}%`);
      }
      
      const overallText = document.querySelector('.overall-progress-text');
      if (overallText) {
        overallText.textContent = `${Math.round(overallPercent)}%`;
      }
    }, 300);
    
    // Overall interval'i kaydet
    activeUploads.current.overallInterval = overallInterval;

    for (const photo of pendingPhotos) {
      // İptal edildiyse döngüden çık
      if (activeUploads.current.canceled) {
        break;
      }
      
      const result = await uploadPhoto(photo);
      
      if (result === true) {
        successCount++;
      } else if (result === 'LIMIT_ERROR') {
        setUploadError('Photo upload limit reached. Please contact the event owner.');
        clearInterval(overallInterval);
        break;
      }
    }

    // İşlem bittiğinde overall progress'i %100 yap, eğer iptal edilmediyse
    clearInterval(overallInterval);
    activeUploads.current.overallInterval = undefined;
    
    if (!activeUploads.current.canceled) {
      setOverallProgress(100);

      if (successCount > 0) {
        if (successCount === totalCount) {
          toast.success(`All ${successCount} photos uploaded successfully`);
        } else {
          toast.success(`${successCount} of ${totalCount} photos uploaded successfully`);
        }

        setUploadComplete(true);
        
        // Önce onPhotoUploadComplete'i çağır
        if (onPhotoUploadComplete) {
          await onPhotoUploadComplete();
        }

        // Sonra kısa bir gecikmeyle modalı kapat
        setTimeout(() => {
          setSelectedPhotos([]);
          setUploadComplete(false);
          setUploadError(null);
          setOverallProgress(0);
          onClose();
        }, 1500); // Biraz daha uzun bir süre verdim ki kullanıcı başarılı mesajını görebilsin
      } else if (!uploadError) {
        setUploadError('Failed to upload photos. Please try again later.');
        toast.error('Failed to upload photos');
      }
    }

    setIsUploading(false);
  };

  if (!isOpen) return null;

  const isAtMaxLimit = selectedPhotos.length >= MAX_PHOTOS;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50">
      <div className="bg-white rounded-lg w-full max-w-xl mt-6">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-medium mb-0.5">Upload Photos</h2>
            <p className="text-xs text-gray-500">Add photos to {title} (max {MAX_PHOTOS})</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isUploading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Upload Area */}
        <div className="p-4" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          {/* Warnings and Status Messages */}
          {showLimitWarning && (
            <div className="mb-3 p-2 bg-amber-50 rounded border border-amber-200 flex items-center gap-2 text-amber-800 text-xs">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span>You can upload a maximum of {MAX_PHOTOS} photos at once.</span>
            </div>
          )}
          
          {showFormatWarning && (
            <div className="mb-3 p-2 bg-amber-50 rounded border border-amber-200 flex items-center gap-2 text-amber-800 text-xs">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span>Only JPG, PNG, HEIC and HEIF images are allowed.</span>
            </div>
          )}
          
          {uploadComplete && (
            <div className="mb-3 p-2 bg-green-50 rounded border border-green-200 flex items-center gap-2 text-green-800 text-xs">
              <Check className="w-4 h-4 text-green-500" />
              <span>Upload complete! All photos were successfully uploaded.</span>
            </div>
          )}
          
          {uploadError && (
            <div className="mb-3 p-2 bg-red-50 rounded border border-red-200 flex items-center gap-2 text-red-800 text-xs">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span>{uploadError}</span>
            </div>
          )}
          
          {isUploading && (
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Uploading photos...</span>
                <span className="text-xs font-medium text-gray-700 overall-progress-text">{overallProgress}%</span>
              </div>
              <div className="bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-[#84cc16] h-full rounded-full transition-all duration-300 ease-out overall-progress-bar"
                  style={{ width: `${overallProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        
          {/* Drag & Drop Zone */}
          <div
            onDragEnter={!isUploading ? handleDragEnter : undefined}
            onDragOver={!isUploading ? handleDragEnter : undefined}
            onDragLeave={!isUploading ? handleDragLeave : undefined}
            onDrop={!isUploading ? handleDrop : undefined}
            className={`
              border border-dashed rounded-md p-4 mb-4 text-center
              ${isDragging ? 'border-[#84cc16] bg-[#84cc16]/5' : isAtMaxLimit || isUploading ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-gray-200'}
              ${isAtMaxLimit || isUploading ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex flex-col items-center gap-2">
              <div className={`w-9 h-9 rounded-full ${isAtMaxLimit || isUploading ? 'bg-gray-200' : 'bg-gray-100'} flex items-center justify-center`}>
                {isUploading ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                ) : (
                  <Upload className={`w-4 h-4 ${isAtMaxLimit ? 'text-gray-400' : 'text-gray-400'}`} />
                )}
              </div>
              <div>
                {isUploading ? (
                  <p className="text-sm mb-0.5 text-gray-500">
                    Uploading {selectedPhotos.length} photos...
                  </p>
                ) : isAtMaxLimit ? (
                  <p className="text-sm mb-0.5 text-gray-500">
                    Maximum {MAX_PHOTOS} photos reached. Remove some to add more.
                  </p>
                ) : (
                  <p className="text-sm mb-0.5">
                    Drag and drop your photos here, or{' '}
                    <label className="text-[#84cc16] cursor-pointer hover:text-[#84cc16]/80">
                      browse
                      <input
                        type="file"
                        className="hidden"
                        multiple
                        accept="image/jpeg,image/png"
                        onChange={handleFileSelect}
                        disabled={isAtMaxLimit || isUploading}
                      />
                    </label>
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {selectedPhotos.length} of {MAX_PHOTOS} photos selected • Supported formats: JPG, PNG
                </p>
              </div>
            </div>
          </div>

          {/* Selected Photos */}
          {selectedPhotos.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Selected Photos</h3>
                <p className="text-xs text-gray-500">{selectedPhotos.length} of {MAX_PHOTOS}</p>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {selectedPhotos.map(photo => (
                  <div key={photo.id} className="relative group">
                    <div className="relative">
                      <img
                        src={photo.preview}
                        alt="Preview"
                        className={`w-full aspect-square object-cover rounded-md ${photo.uploadStatus === 'error' ? 'opacity-70' : ''}`}
                      />
                      
                      {/* Upload status indicators */}
                      {photo.uploadStatus === 'uploading' && photo.uploadProgress !== undefined && (
                        <>
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-md">
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                              <span id={`progress-text-${photo.id}`} className="text-white text-xs font-medium">{photo.uploadProgress}%</span>
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
                            <div 
                              id={`progress-${photo.id}`}
                              className="bg-white h-full transition-all duration-300 ease-out"
                              style={{ width: `${photo.uploadProgress}%` }}
                            ></div>
                          </div>
                        </>
                      )}
                      
                      {photo.uploadStatus === 'success' && (
                        <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      )}
                      
                      {photo.uploadStatus === 'error' && (
                        <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white">
                          <X className="w-3.5 h-3.5" />
                        </div>
                      )}
                    </div>
                    
                    {/* Remove button */}
                    {!isUploading && (
                      <button
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={isUploading}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    
                    {/* Error message */}
                    {photo.uploadStatus === 'error' && photo.errorMessage && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-black/70 p-1.5 rounded text-white text-[10px] max-w-[90%] text-center">
                          {photo.errorMessage}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-lg flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="h-8 text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={selectedPhotos.length === 0 || isUploading || uploadComplete}
            className="h-8 text-xs"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Uploading...
              </>
            ) : uploadComplete ? (
              <>
                <Check className="w-3.5 h-3.5 mr-1.5" />
                Uploaded
              </>
            ) : (
              `Upload (${selectedPhotos.length})`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadPhotoGuest; 